import { useEffect } from 'react'
import { WeeklyMealPlanner } from '@/components/meal-planning/weekly-meal-planner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useMealPlanningStore } from '@/stores/meal-planning-store'
import { useRecipeStore } from '@/stores/recipe-store'
import { useHouseholdStore } from '@/stores/household-store'
import { CalendarDays, ChefHat, BookOpen } from 'lucide-react'

export default function MealPlanningPage() {
  const { loading, error, loadMealPlans } = useMealPlanningStore()
  const { recipes, loadRecipes } = useRecipeStore()
  const { currentHousehold, viewMode } = useHouseholdStore()

  const householdId = viewMode === 'household' ? currentHousehold?.id : undefined

  useEffect(() => {
    void loadMealPlans(householdId)
    void loadRecipes()
  }, [householdId, loadMealPlans, loadRecipes])

  if (loading) {
    return (
      <div className="container py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Weekplanning laden...</h2>
            <p className="text-gray-600">Even geduld terwijl we je maaltijdplanning voorbereiden.</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container py-8">
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-800 flex items-center gap-2">
              <ChefHat className="h-5 w-5" />
              Fout bij laden van weekplanning
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-red-700 mb-4">{error}</p>
            <div className="flex gap-2">
              <Button onClick={() => void loadMealPlans(householdId)} variant="outline">
                Opnieuw proberen
              </Button>
              <Button onClick={() => window.location.reload()}>Pagina vernieuwen</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container py-6">
      <div className="mb-6 space-y-3">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <CalendarDays className="h-8 w-8 text-orange-500" />
          Weekplanning
        </h1>
        <p className="max-w-3xl text-gray-600">
          Kies een week, selecteer een dag en plan daar direct een recept in. Zo blijft je weekoverzicht helder en
          rustig.
        </p>
      </div>

      {recipes.length === 0 && (
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <BookOpen className="h-8 w-8 text-blue-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">Welkom bij je weekplanning</h3>
                <p className="text-blue-800 mb-4">
                  Voeg eerst wat recepten toe om je weekplanning te kunnen gebruiken.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <WeeklyMealPlanner className="min-h-[600px]" />
    </div>
  )
}
